import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { createService, updateService, getServiceDetail } from "apis";
import { useAppModal } from "components/common/AppModalProvider";
import { ServiceCategory } from "types";

interface ScheduleInput {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
}

let scheduleIdCounter = 0;

const categoryOptions: { label: string; value: ServiceCategory }[] = [
  { label: "💬 수다·티타임", value: ServiceCategory.CHAT },
  { label: "🎮 게임·듀오", value: ServiceCategory.GAME },
  { label: "🔮 타로·운세", value: ServiceCategory.TAROT },
  { label: "💡 고민·피드백", value: ServiceCategory.FEEDBACK },
  { label: "💻 랜선 스터디", value: ServiceCategory.STUDY },
  { label: "✨ 자유주제", value: ServiceCategory.FREE },
];

export default function StudioServiceForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const { showAlert } = useAppModal();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ServiceCategory>(
    ServiceCategory.FREE
  );
  const [price, setPrice] = useState<number>(0);
  const [durationMinutes, setDurationMinutes] = useState<number>(60);
  const [externalLink, setExternalLink] = useState("");
  const [schedules, setSchedules] = useState<ScheduleInput[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Load existing service data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      setIsLoadingData(true);
      getServiceDetail(id)
        .then((response) => {
          const svc = response.data.data;
          setTitle(svc.title);
          setDescription(svc.description);
          setCategory(svc.category);
          setPrice(svc.price);
          setDurationMinutes(svc.durationMinutes);
          setExternalLink((svc as any).externalLink ?? "");
          if (svc.schedules?.length) {
            setSchedules(
              svc.schedules.map((s) => ({
                id: ++scheduleIdCounter,
                date: s.date,
                startTime: s.startTime,
                endTime: s.endTime,
              }))
            );
          }
        })
        .catch(() => {
          showAlert("서비스를 불러올 수 없습니다.", { variant: 'error' });
          navigate("/studio/services");
        })
        .finally(() => setIsLoadingData(false));
    }
  }, [isEditMode, id, navigate]);

  const addSchedule = () => {
    setSchedules((prev) => [
      ...prev,
      { id: ++scheduleIdCounter, date: "", startTime: "", endTime: "" },
    ]);
  };

  const removeSchedule = (schedId: number) => {
    setSchedules((prev) => prev.filter((s) => s.id !== schedId));
  };

  const updateScheduleField = (
    schedId: number,
    field: keyof Omit<ScheduleInput, "id">,
    value: string
  ) => {
    setSchedules((prev) =>
      prev.map((s) => (s.id === schedId ? { ...s, [field]: value } : s))
    );
  };

  const isFormValid =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    price > 0 &&
    durationMinutes > 0 &&
    externalLink.trim().length > 0;

  const handleSubmit = async () => {
    if (!isFormValid) return;
    setIsSubmitting(true);
    try {
      if (isEditMode && id) {
        await updateService(
          id,
          title,
          description,
          price,
          category,
          externalLink,
          durationMinutes
        );
      } else {
        await createService(
          title,
          description,
          price,
          category,
          externalLink,
          durationMinutes,
          schedules
            .filter((s) => s.date && s.startTime && s.endTime)
            .map(({ date, startTime, endTime }) => ({
              date,
              startTime,
              endTime,
            }))
        );
      }
      queryClient.invalidateQueries(["studioMyServices"]);
      navigate("/studio/services");
    } catch (error) {
      console.error("Service save failed:", error);
      showAlert(isEditMode ? "서비스 수정 실패" : "서비스 등록 실패", { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/studio/services")}
            className="text-achromatic-400 hover:text-achromatic-600 transition-colors text-sm"
          >
            ← 돌아가기
          </button>
          <h2 className="text-lg font-extrabold text-achromatic-900">
            {isEditMode ? "서비스 수정" : "새 서비스 등록"}
          </h2>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl border border-achromatic-50 p-6">
        <div className="flex flex-col gap-5 max-w-[600px]">
          {/* Title */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-achromatic-900">
              서비스명 *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 1:1 영어 회화 레슨"
              className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-200 transition-all"
            />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-achromatic-900">
              설명 *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="서비스에 대한 상세 설명을 입력하세요"
              rows={4}
              className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
            />
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-achromatic-900">
              카테고리 *
            </label>
            <div className="flex flex-wrap gap-2">
              {categoryOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setCategory(opt.value)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all border ${
                    category === opt.value
                      ? "border-primary-500 bg-primary-50 text-primary-600"
                      : "border-achromatic-100 text-achromatic-500 hover:border-achromatic-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Price & Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-achromatic-900">
                가격 (원) *
              </label>
              <input
                type="number"
                value={price || ""}
                onChange={(e) => setPrice(parseInt(e.target.value) || 0)}
                placeholder="10000"
                min="0"
                className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-200 transition-all"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-achromatic-900">
                세션 시간 (분) *
              </label>
              <input
                type="number"
                value={durationMinutes || ""}
                onChange={(e) =>
                  setDurationMinutes(parseInt(e.target.value) || 0)
                }
                placeholder="60"
                min="10"
                className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-200 transition-all"
              />
            </div>
          </div>

          {/* External Link */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-achromatic-900">
              외부 접속 링크 *
            </label>
            <input
              type="url"
              value={externalLink}
              onChange={(e) => setExternalLink(e.target.value)}
              placeholder="https://zoom.us/j/..."
              className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-200 transition-all"
            />
            <p className="text-[11px] text-achromatic-400">
              Zoom, 디스코드, Google Meet, 오픈카톡 등의 링크를 입력하세요. 결제 완료 후에만
              게스트에게 공개됩니다.
            </p>
          </div>

          {/* Schedule Slots (create mode only — update API doesn't support schedules) */}
          {!isEditMode && (
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-semibold text-achromatic-900">
                  예약 가능 시간대 ({schedules.length}개)
                </label>
                <button
                  onClick={addSchedule}
                  className="text-xs font-bold text-primary-500 hover:text-primary-600 transition-colors"
                >
                  + 시간대 추가
                </button>
              </div>

              {schedules.length === 0 && (
                <div className="text-center py-6 border border-dashed border-achromatic-200 rounded-xl text-achromatic-300">
                  <p className="text-sm mb-1">시간대를 추가해주세요</p>
                  <button
                    onClick={addSchedule}
                    className="text-sm font-bold text-primary-500 hover:text-primary-600"
                  >
                    + 첫 번째 시간대 추가
                  </button>
                </div>
              )}

              {schedules.map((sch) => (
                <div
                  key={sch.id}
                  className="flex items-center gap-2 p-3 bg-achromatic-50 rounded-xl"
                >
                  <input
                    type="date"
                    value={sch.date}
                    onChange={(e) =>
                      updateScheduleField(sch.id, "date", e.target.value)
                    }
                    className="flex-1 border border-achromatic-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-primary-500"
                  />
                  <input
                    type="time"
                    value={sch.startTime}
                    onChange={(e) =>
                      updateScheduleField(sch.id, "startTime", e.target.value)
                    }
                    className="border border-achromatic-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-primary-500"
                  />
                  <span className="text-achromatic-400 text-xs">~</span>
                  <input
                    type="time"
                    value={sch.endTime}
                    onChange={(e) =>
                      updateScheduleField(sch.id, "endTime", e.target.value)
                    }
                    className="border border-achromatic-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-primary-500"
                  />
                  <button
                    onClick={() => removeSchedule(sch.id)}
                    className="text-red-400 hover:text-red-600 transition-colors text-sm font-bold px-1"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {isEditMode && (
            <div className="bg-achromatic-50 rounded-xl p-4">
              <p className="text-xs text-achromatic-400">
                💡 스케줄은 서비스 관리 목록에서 확인할 수 있습니다.
                기존 스케줄을 유지하면서 서비스 정보를 수정합니다.
              </p>
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-3 pt-4 border-t border-achromatic-100">
            <button
              onClick={() => navigate("/studio/services")}
              className="px-6 py-3 text-sm font-semibold text-achromatic-500 bg-achromatic-50 rounded-xl hover:bg-achromatic-100 transition-colors"
            >
              취소
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
              className="px-6 py-3 text-sm font-bold text-white bg-primary-500 rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? "저장 중..."
                : isEditMode
                ? "서비스 수정"
                : "서비스 등록"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
